import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal, Button } from "@lynx/ui-components";
import cn from "classnames";

import { payoutThunks } from "actions-thunks/payout-thunks";
import { PAYOUT_SETTINGS_ERROR, TO_BANK_ACCOUNT_DETAILS } from "reducers/types";
import { utilityFunctions } from "../utilities/utilityFunctions";
import BankAccountCard from "./components/BankAccountCard";
import { parseAmountInput } from "./wireUtils";

import "./payout-modals.scss";

/** Enable / edit the automatic monthly payout (Minimum PB only; bank account implied). */
const AutoPayoutSettingModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentAccount, paymentMethods, payoutSettings } = useSelector(
    (state) => state.accounts
  );
  const { loading } = useSelector((state) => state.utils);
  const { errorMessage } = useSelector((state) => state.errors);
  const serverError = errorMessage?.find((i) => i.type === PAYOUT_SETTINGS_ERROR)?.message || "";

  const current = payoutSettings?.autoPayout || {};
  const bankAccount = paymentMethods?.bankAccount || null;

  const [minimumPB, setMinimumPB] = useState(
    typeof current.minimumPB === "number"
      ? utilityFunctions.currencyFormatter().format(current.minimumPB)
      : ""
  );
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);

  // settings may load after the modal opened: fill the saved value once
  useEffect(() => {
    if (!touched && !minimumPB && typeof current.minimumPB === "number") {
      setMinimumPB(utilityFunctions.currencyFormatter().format(current.minimumPB));
    }
  }, [current.minimumPB, minimumPB, touched]);

  const validate = (value) => {
    const numeric = parseAmountInput(value);
    if (!value || String(value).trim() === "") return "Please enter an amount";
    if (isNaN(numeric) || numeric < 0) return "Please enter a valid amount";
    return "";
  };

  const handlePBChange = (e) => {
    const value = e.target.value;
    setTouched(true);
    setMinimumPB(value);
    setError(value.trim() ? validate(value) : "");
  };

  const handlePBBlur = () => {
    const numeric = parseAmountInput(minimumPB);
    if (!isNaN(numeric) && numeric >= 0) {
      setMinimumPB(utilityFunctions.currencyFormatter().format(numeric));
    }
  };

  const handleSave = async () => {
    const message = validate(minimumPB);
    setError(message);
    if (message || !bankAccount) return;
    try {
      await dispatch(
        payoutThunks.updateAutoPayoutTC(currentAccount._id, {
          enabled: true,
          minimumPB: parseAmountInput(minimumPB),
          paymentMethod: bankAccount._id,
        })
      );
      onClose();
    } catch (e) {
      // the server message is shown in the modal
    }
  };

  const goToBankDetails = () => {
    onClose();
    navigate(TO_BANK_ACCOUNT_DETAILS);
  };

  const bankAction = bankAccount
    ? { label: "Change bank details", onClick: goToBankDetails }
    : { label: "Set bank details", onClick: goToBankDetails };

  const isDisabled = loading || !minimumPB || !!error || !bankAccount;

  return (
    <div className="modal-window">
      <Modal
        className="payout-modal auto-payout-modal cabinet-modals"
        overlayClassName="request-overlay"
        styleType="zimtra-primary"
        isOpen={true}
        hideBlur
        hasCloseIcon={false}
        onClickOutside={onClose}
      >
        <Modal.Header
          className="modal-inner-header"
          onClose={onClose}
          title={<h2 className="zimtra-h2">Automatic monthly payout settings</h2>}
        />
        <Modal.Body className="modal-inner-body">
          <div className="payout-row">
            <div className="payout-label">Minimum Performance Bond to maintain</div>
            <div className={cn("payout-field", { "error-field": error })}>
              <input
                id="auto-payout-minimum-pb"
                type="text"
                className="payout-input"
                value={minimumPB}
                placeholder="$0.00"
                onChange={handlePBChange}
                onBlur={handlePBBlur}
              />
              {error && <div className="error-text">{error}</div>}
            </div>
          </div>

          <p className="payout-text payout-text-muted">
            Processed monthly. This setting stays active until you change or disable it.
          </p>

          <BankAccountCard bank={bankAccount} action={bankAction} />

          {serverError && <div className="payout-server-error">{serverError}</div>}
        </Modal.Body>
        <Modal.Footer className="modal-inner-footer d-flex justify-content-center">
          <Button
            size="md"
            id="auto-payout-save-btn"
            buttonStyle="button-variant-second"
            buttonClassName="cabinet-primary-btn"
            disabled={isDisabled}
            onClick={handleSave}
            label="Save settings"
          />
          <Button
            size="md"
            id="auto-payout-cancel-btn"
            buttonStyle="button-variant-fourth"
            buttonClassName="cabinet-secondary-btn"
            onClick={onClose}
            label="Cancel"
          />
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AutoPayoutSettingModal;
